import { gql } from "generated-graphql";

export const WASTE_SUBMISSIONS_FOR_STAFF_QUERY = gql(`
  query WasteSubmissionsForStaff(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
    $includeOverdue: Boolean
    $onlyOverdue: Boolean
    $startDate: DateTime
    $endDate: DateTime
    $materialized: Boolean
    $materializationTypes: [SubmissionMaterializationType!]
  ) {
    wasteSubmissionsForStaff(
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
      includeOverdue: $includeOverdue
      onlyOverdue: $onlyOverdue
      startDate: $startDate
      endDate: $endDate
      materialized: $materialized
      materializationTypes: $materializationTypes
    ) {
      items {
        # Virtual event identifiers
        id
        taskId
        seriesId
        deadline
        virtualEventStatus
        taskFrequency
        materializationType

        # Cross-tenant data
        tenantId
        tenantName

        # Facility data
        facilities {
          id
          name
          state
        }
        facilityName
        state
        epaIds
        generatorStatus

        # Submission data (may be null for NOT_STARTED)
        submissionId
        submissionStatus
        submissionType
        title

        # Staff assignments
        staffAssigneeId
        ownerName
        ownerEmail
        staffPeerReviewerId
        peerReviewerName
        peerReviewerEmail

        # Workflow state
        waitingOn {
          id
          email
          firstName
          lastName
          isSigner
        }

        # Last comment info
        lastComment
        lastCommentAt
        lastCommentByName

        # Activity tracking
        lastActivity
      }
      count
    }
  }
`);

export const WASTE_SUBMISSIONS_STAFF_SUMMARY_QUERY = gql(`
  query WasteSubmissionsStaffSummary(
    $search: String
    $includeOverdue: Boolean
    $onlyOverdue: Boolean
    $startDate: DateTime
    $endDate: DateTime
    $materialized: Boolean
    $materializationTypes: [SubmissionMaterializationType!]
  ) {
    wasteSubmissionsStaffSummary(
      search: $search
      includeOverdue: $includeOverdue
      onlyOverdue: $onlyOverdue
      startDate: $startDate
      endDate: $endDate
      materialized: $materialized
      materializationTypes: $materializationTypes
    ) {
      byStatus {
        status
        count
      }
      byState {
        state
        count
      }
      byStateAndStatus {
        state
        total
        byStatus {
          status
          count
        }
      }
      byTenant {
        tenantId
        tenantName
        count
      }
      byAssignee {
        assigneeId
        assigneeName
        assigneeEmail
        count
      }
      total
      overdue
    }
  }
`);

// Mutations now support both submissionIds and virtualEventIds
// Virtual events will be materialized before applying the action

export const SET_SUBMISSION_PEER_REVIEWERS_MUTATION = gql(`
  mutation SetSubmissionPeerReviewers(
    $submissionIds: [ID!]!
    $virtualEventIds: [ID!]
    $peerReviewerId: ID!
  ) {
    setSubmissionPeerReviewers(
      submissionIds: $submissionIds
      virtualEventIds: $virtualEventIds
      peerReviewerId: $peerReviewerId
    ) {
      succeeded
      updated
      failed
    }
  }
`);

export const CLEAR_SUBMISSION_ASSIGNEES_MUTATION = gql(`
  mutation ClearSubmissionAssignees(
    $submissionIds: [ID!]!
    $virtualEventIds: [ID!]
  ) {
    clearSubmissionAssignees(
      submissionIds: $submissionIds
      virtualEventIds: $virtualEventIds
    ) {
      succeeded
      updated
      failed
    }
  }
`);

export const CLEAR_SUBMISSION_PEER_REVIEWERS_MUTATION = gql(`
  mutation ClearSubmissionPeerReviewers(
    $submissionIds: [ID!]!
    $virtualEventIds: [ID!]
  ) {
    clearSubmissionPeerReviewers(
      submissionIds: $submissionIds
      virtualEventIds: $virtualEventIds
    ) {
      succeeded
      updated
      failed
    }
  }
`);

export const SET_SUBMISSION_ASSIGNEES_STAFF_MUTATION = gql(`
  mutation SetSubmissionAssigneesStaff(
    $submissionIds: [ID!]!
    $virtualEventIds: [ID!]
    $assigneeId: ID!
  ) {
    setSubmissionAssignees(
      submissionIds: $submissionIds
      virtualEventIds: $virtualEventIds
      assigneeId: $assigneeId
    ) {
      succeeded
      updated
      failed
    }
  }
`);

// Query for staff users (used in assignment dialogs)
export const STAFF_USERS_QUERY = gql(`
  query StaffUsersForAssignment($search: String) {
    staffUsers(search: $search, pageSize: 50) {
      items {
        id
        email
        person {
          first
          last
        }
      }
    }
  }
`);

// Helper to check if an ID is a virtual event ID (taskId_deadline format)
export function isVirtualEventId(id: string): boolean {
  return id.includes("_");
}

// Helper to separate submission IDs and virtual event IDs from a mixed array
export function separateIds(ids: string[]): {
  submissionIds: string[];
  virtualEventIds: string[];
} {
  const submissionIds: string[] = [];
  const virtualEventIds: string[] = [];

  for (const id of ids) {
    if (isVirtualEventId(id)) {
      virtualEventIds.push(id);
    } else {
      submissionIds.push(id);
    }
  }

  return { submissionIds, virtualEventIds };
}

// Mutation to reset materialized submissions back to virtual events
export const RESET_MATERIALIZED_SUBMISSIONS_MUTATION = gql(`
  mutation ResetMaterializedSubmissions($submissionIds: [ID!]!) {
    resetMaterializedSubmissions(submissionIds: $submissionIds) {
      succeeded
      reset
      skipped
      skippedIds
    }
  }
`);
